import React from 'react';

// =============================================================================
// GridCountCell
// =============================================================================
// The shared count cell for the LC dashboard's matrix-style panels (Delivery
// Schedule, Tracking, …). Renders a single right-aligned count that is clickable
// when it has a value and a handler — clicking opens that cell's drill-down. The
// color *rule* lives in each panel (which knows what a cell means); the cell just
// applies the `colorClass` it's handed and picks a hover that stays readable on top
// of it: a light-blue wash on a plain cell, a slight darken on a colored (white-text)
// cell so the number doesn't wash out.

export interface GridCountCellProps {
  value: number;
  // Fired when the cell is clicked. Omitted (or value <= 0) → not clickable.
  onSelect?: () => void;
  bold?: boolean;
  // Background/text color classes for the cell, e.g. 'bg-red-600 text-white'.
  // Empty string = plain (no fill).
  colorClass?: string;
  // Show a '…' placeholder instead of the number (initial load, before data).
  placeholder?: boolean;
  title?: string;
}

const GridCountCell: React.FC<GridCountCellProps> = ({
  value,
  onSelect,
  bold = false,
  colorClass = '',
  placeholder = false,
  title,
}) => {
  const base = `block w-full text-right tabular-nums px-2 py-1 ${bold ? 'font-bold' : ''} ${colorClass}`;
  const clickable = !!onSelect && value > 0;

  if (!clickable) {
    return <span className={base}>{placeholder ? '…' : value.toLocaleString()}</span>;
  }

  // Colored cells (white text) get a darken-on-hover so the number stays legible;
  // plain cells get the light-blue tint.
  const hoverCls = colorClass
    ? 'hover:brightness-90'
    : 'hover:bg-blue-50 dark:hover:bg-slate-700/40';

  return (
    <button type="button" onClick={onSelect} className={`${base} ${hoverCls} transition`} title={title}>
      {value.toLocaleString()}
    </button>
  );
};

export default GridCountCell;
